import React, { useCallback, useEffect, useRef } from 'react';

import { useIntl } from 'react-intl';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, xcss } from '@atlaskit/primitives';

import { SearchLinkSkeletons } from '../../../../common/ui/search-link-skeletons';
import { onDropdownDwelled } from '../../../../common/utils/events/dropdown-dwelled';
import { useAnalytics, useCommonAndPrivateAttributes } from '../../../../controllers/analytics';
import { useFetchAll } from '../../../../controllers/dropdown/use-fetch-all';
import { useDropdownLoading } from '../../../../controllers/store/dropdown';

import { AutoSuggestion } from './auto-suggestion';
import { messages } from './messages';
import { MimicQuery } from './mimic-query';
import { PeopleAndTeamsResults } from './people-and-teams';
import { RecentSearchResults } from './recent-searches';
import { RecentViews } from './recent-views';

const searchDropdownStyles = xcss({
	padding: 'space.100',
	gap: 'space.025',
	boxShadow: 'elevation.shadow.overlay',
	borderWidth: '0',
	boxSizing: 'border-box',
});

export type SearchDropdownProps = {
	inputWidth: number;
	setLocalQuery: (query: string) => void;
};

export const SearchDropdown = ({ inputWidth }: SearchDropdownProps): React.JSX.Element => {
	useFetchAll();
	const [dropdownLoading] = useDropdownLoading();

	const { fireAnalyticsEvent } = useAnalytics();
	const { commonAttributes, nonPrivacySafeAttributes } = useCommonAndPrivateAttributes();
	const { formatMessage } = useIntl();

	const containerRef = useRef<HTMLObjectElement>(null);

	useEffect(() => {
		const start = Date.now();

		return () => {
			const end = Date.now();
			const msElapsed = end - start;

			fireAnalyticsEvent(
				onDropdownDwelled({
					commonAttributes,
					nonPrivacySafeAttributes,
					dwellTime: msElapsed,
				}),
			);
		};
	}, [commonAttributes, fireAnalyticsEvent, nonPrivacySafeAttributes]);

	const getElementIndex = useCallback(
		(eventTarget?: EventTarget) => {
			if (!eventTarget || !containerRef.current) {
				return;
			}

			const currentChildNodes = containerRef.current.childNodes;

			const childNodeList: ChildNode[] = [];
			for (let i = 0; i < currentChildNodes.length; i++) {
				childNodeList.push(currentChildNodes.item(i));
			}

			// Find which index the selected target is from the childNodes of this component
			const childIndex = childNodeList.findIndex((child) => {
				return child.isSameNode(eventTarget as Node) || child.contains(eventTarget as Node);
			});

			return childIndex === -1 ? undefined : childIndex;
		},
		[containerRef],
	);

	// The direct childNodes of this component are used to get the analytics element index of each
	// SearchLink. Nesting two SearchLinks together into a single direct childNode will result in
	// an incorrect element index. See `getElementIndex` for logic.
	const content = dropdownLoading ? (
		<>
			<MimicQuery getElementIndex={getElementIndex} />
			<SearchLinkSkeletons />
		</>
	) : (
		<>
			<MimicQuery getElementIndex={getElementIndex} />
			<AutoSuggestion getElementIndex={getElementIndex} />
			<RecentSearchResults getElementIndex={getElementIndex} />
			<PeopleAndTeamsResults getElementIndex={getElementIndex} />
			<RecentViews getElementIndex={getElementIndex} />
		</>
	);

	return (
		<Box
			xcss={searchDropdownStyles}
			style={{
				width: `${inputWidth}px`,
			}}
			aria-label={formatMessage(messages.searchSuggestionsDropdown)}
			testId={'search-suggestions-dropdown'}
			ref={containerRef}
		>
			{content}
		</Box>
	);
};
